import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import useAsyncRequest from "hooks/useAsyncRequest";
import {
  LicenseEmailFailureReason,
  LicenseEmailStatus,
  LicenseEmailTriggerType,
  type LicenseEmailDto,
} from "client/api-v2";
import type { Subscription } from "client/api-v2";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { SendLicenseEmailDialog } from "../components/SendLicenseEmailDialog";
import { getLicenseEmailHistory } from "../services/subscriptionService";
import { subscriptionIdRouteParam } from "../subscription-routes";

function formatUtc(dateString: string | DateTime | null | undefined): string {
  if (!dateString) return "—";
  return getOrParseDateTime(dateString).setZone("utc").toFormat("yyyy-MM-dd HH:mm");
}

function triggerLabel(triggerType: LicenseEmailTriggerType): string {
  if (triggerType === LicenseEmailTriggerType.AutomatedFulfilment) return "Automated";
  if (triggerType === LicenseEmailTriggerType.Manual) return "Manual";
  return triggerType;
}

function failureReasonLabel(reason: LicenseEmailFailureReason | undefined | null): string {
  if (reason === LicenseEmailFailureReason.XmlGenerationFailed) return "XML generation failed";
  if (reason === LicenseEmailFailureReason.RecipientResolutionFailed) return "Recipient resolution failed";
  if (reason === LicenseEmailFailureReason.SendGridRejected) return "SendGrid rejected";
  return reason ?? "";
}

function statusColor(status: LicenseEmailStatus): "success" | "error" | "info" | "warning" | "default" {
  switch (status) {
    case LicenseEmailStatus.Sent:
      return "success";
    case LicenseEmailStatus.Failed:
      return "error";
    case LicenseEmailStatus.Sending:
      return "info";
    case LicenseEmailStatus.Pending:
      return "warning";
    case LicenseEmailStatus.Suppressed:
      return "default";
    default:
      return "default";
  }
}

const SKELETON_COLUMNS = Array.from({ length: 8 }, (_, i) => i);
const SKELETON_ROWS = Array.from({ length: 3 }, (_, i) => i);

export function SubscriptionLicenseEmails() {
  const { [subscriptionIdRouteParam]: subscriptionId } = useParams<Record<string, string>>();
  const { entity: subscription } = useEntity<Subscription>();
  const { isAuthorized } = useRbacContext();
  const canSend = isAuthorized(RbacPolicies.ServerLicenses.ReadWrite);

  const [retryEmail, setRetryEmail] = useState<LicenseEmailDto | null>(null);

  const { data: emails, loading, error, refresh } = useAsyncRequest(getLicenseEmailHistory, subscriptionId ?? "");

  const retryRecipients = useMemo(
    () =>
      retryEmail
        ? { to: retryEmail.toRecipients, cc: retryEmail.ccRecipients, bcc: retryEmail.bccRecipients }
        : undefined,
    [retryEmail]
  );

  const handleRetrySent = () => {
    setRetryEmail(null);
    refresh();
  };

  const sortedEmails = useMemo(
    () =>
      emails
        ? [...emails].sort(
            (a, b) => getOrParseDateTime(b.createdDate).toMillis() - getOrParseDateTime(a.createdDate).toMillis()
          )
        : [],
    [emails]
  );

  return (
    <DetailsPageLayout pageName="License Email History">
      {loading && !emails && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {SKELETON_COLUMNS.map((i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {SKELETON_ROWS.map((row) => (
                <TableRow key={row}>
                  {SKELETON_COLUMNS.map((col) => (
                    <TableCell key={col}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {error && <Typography color="error">Failed to load license email history.</Typography>}
      {!error && emails && emails.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            opacity: loading ? 0.6 : 1,
            transition: "opacity 0.15s ease",
          }}
        >
          No license emails have been sent for this subscription.
        </Typography>
      )}
      {!error && emails && emails.length > 0 && (
        <Box
          sx={{
            overflowX: "auto",
            opacity: loading ? 0.6 : 1,
            transition: "opacity 0.15s ease",
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Queued</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Failure Reason</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEmails.map((email) => (
                <TableRow key={email.id}>
                  <TableCell>
                    {email.status === LicenseEmailStatus.Suppressed ? (
                      <Tooltip title="Automated Fulfillment is turned off for this deal's Zuora quote, so no email was sent automatically. You can still send one manually.">
                        <Chip
                          label={email.status}
                          color={statusColor(email.status)}
                          size="small"
                          sx={{ cursor: "help" }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip label={email.status} color={statusColor(email.status)} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatUtc(email.createdDate)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatUtc(email.sentAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{triggerLabel(email.triggerType)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {email.toRecipients.slice(0, 2).join(", ")}
                      {email.toRecipients.length > 2 && (
                        <Tooltip title={email.toRecipients.slice(2).join(", ")}>
                          <span style={{ cursor: "help" }}>{` and ${email.toRecipients.length - 2} more`}</span>
                        </Tooltip>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{email.requestedBy}</Typography>
                  </TableCell>
                  <TableCell>
                    {email.status === LicenseEmailStatus.Failed &&
                      (email.lastError ? (
                        <Tooltip title={email.lastError}>
                          <Typography variant="body2" sx={{ cursor: "help" }}>
                            {failureReasonLabel(email.failureReason)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">{failureReasonLabel(email.failureReason)}</Typography>
                      ))}
                  </TableCell>
                  <TableCell>
                    {email.status === LicenseEmailStatus.Failed && (
                      <Button size="small" variant="outlined" onClick={() => setRetryEmail(email)} disabled={!canSend}>
                        Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <SendLicenseEmailDialog
        open={retryEmail !== null}
        subscriptionId={subscriptionId ?? ""}
        subscriptionName={subscription?.friendlyName ?? subscriptionId ?? ""}
        handleClose={() => setRetryEmail(null)}
        onSent={handleRetrySent}
        initialRecipients={retryRecipients}
      />
    </DetailsPageLayout>
  );
}
